import { Capacitor } from '@capacitor/core';
import { FirebaseCrashlytics } from '@capacitor-community/firebase-crashlytics';

class CrashlyticsService {
  constructor() {
    if (!CrashlyticsService.instance) {
      this.isInitialized = false;
      CrashlyticsService.instance = this;
    }
    return CrashlyticsService.instance;
  }

  static getInstance() {
    if (!CrashlyticsService.instance) {
      CrashlyticsService.instance = new CrashlyticsService();
    }
    return CrashlyticsService.instance;
  }

  /**
   * Initialize the Crashlytics service
   */
  async initialize() {
    if (this.isInitialized) {
      return;
    }

    try {
      if (Capacitor.isNativePlatform()) {
        // Initialize native Crashlytics
        await FirebaseCrashlytics.setEnabled({ enabled: true });
      }
      
      this.isInitialized = true;
    } catch (error) {
      // Silently handle initialization errors
    }
  }

  /**
   * Set user information for crash reports
   */
  async setUser(user) {
    try {
      if (Capacitor.isNativePlatform()) {
        await FirebaseCrashlytics.setUserId({ userId: user.id });
        
        if (user.email) {
          await FirebaseCrashlytics.setContext({ 
            key: 'user_email', 
            value: user.email, 
            type: 'string' 
          });
        }
        
        if (user.name) {
          await FirebaseCrashlytics.setContext({ 
            key: 'user_name', 
            value: user.name, 
            type: 'string' 
          });
        }
      }
    } catch (error) {
      // Silently handle errors
    }
  }

  /**
   * Set custom key-value pairs for crash reports
   */
  async setCustomKey(key, value) {
    try {
      if (Capacitor.isNativePlatform()) {
        const type = typeof value === 'string' ? 'string' : 
                    typeof value === 'number' ? 'double' : 'boolean';
        await FirebaseCrashlytics.setContext({ key, value, type });
      }
    } catch (error) {
      // Silently handle errors
    }
  }

  /**
   * Set multiple custom key-value pairs
   */
  async setCustomKeys(keys) {
    try {
      if (Capacitor.isNativePlatform()) {
        for (const { key, value } of keys) {
          const type = typeof value === 'string' ? 'string' : 
                      typeof value === 'number' ? 'double' : 'boolean';
          await FirebaseCrashlytics.setContext({ key, value, type });
        }
      }
    } catch (error) {
      // Silently handle errors
    }
  }

  /**
   * Log a message to crash reports
   */
  async log(message) {
    try {
      if (Capacitor.isNativePlatform()) {
        await FirebaseCrashlytics.addLogMessage({ message });
      }
    } catch (error) {
      // Silently handle errors
    }
  }

  /**
   * Log an error to crash reports
   */
  async recordError(error, context) {
    try {
      if (Capacitor.isNativePlatform()) {
        // Combine message and stack trace for better debugging
        const errorMessage = error.message || 'Unknown error';
        const stackTrace = error.stack || 'No stack trace available';
        const fullErrorDetails = `${errorMessage}\n\nStack Trace:\n${stackTrace}`;
        
        await FirebaseCrashlytics.recordException({ 
          message: fullErrorDetails
        });
        
        // Add context as custom key if provided
        if (context) {
          await FirebaseCrashlytics.setContext({
            key: 'error_context',
            value: context,
            type: 'string'
          });
        }
        
        // Mark as fatal error
        await FirebaseCrashlytics.setContext({
          key: 'error_type',
          value: 'fatal',
          type: 'string'
        });
      }
    } catch (crashlyticsError) {
      // Silently handle errors
    }
  }

  /**
   * Log a non-fatal error
   */
  async recordNonFatalError(error, context) {
    try {
      if (Capacitor.isNativePlatform()) {
        // Combine message and stack trace for better debugging
        const errorMessage = error.message || 'Unknown error';
        const stackTrace = error.stack || 'No stack trace available';
        const fullErrorDetails = `${errorMessage}\n\nStack Trace:\n${stackTrace}`;
        
        await FirebaseCrashlytics.recordException({ 
          message: fullErrorDetails
        });
        
        // Add context as custom key if provided
        if (context) {
          await FirebaseCrashlytics.setContext({
            key: 'error_context',
            value: context,
            type: 'string'
          });
        }
        
        // Mark as non-fatal error
        await FirebaseCrashlytics.setContext({
          key: 'error_type',
          value: 'non_fatal',
          type: 'string'
        });
      }
    } catch (crashlyticsError) {
      // Silently handle errors
    }
  }

  /**
   * Enable or disable crash reporting
   */
  async setEnabled(enabled) {
    try {
      if (Capacitor.isNativePlatform()) {
        await FirebaseCrashlytics.setEnabled({ enabled });
      }
    } catch (error) {
      // Silently handle errors
    }
  }

  /**
   * Check if crash reporting is enabled
   */
  async isEnabled() {
    try {
      if (Capacitor.isNativePlatform()) {
        const result = await FirebaseCrashlytics.isEnabled();
        return result.enabled;
      } else {
        // For web, assume enabled
        return true;
      }
    } catch (error) {
      return false;
    }
  }

  /**
   * Force a crash for testing (only in debug builds)
   */
  async crash() {
    try {
      if (Capacitor.isNativePlatform()) {
        await FirebaseCrashlytics.crash({ message: 'Test crash triggered by app' });
      } else {
        // For web, throw an error
        throw new Error('Test crash for web platform');
      }
    } catch (error) {
      // Silently handle errors
    }
  }

  /**
   * Set the app version for crash reports
   */
  async setAppVersion(version) {
    try {
      if (Capacitor.isNativePlatform()) {
        await FirebaseCrashlytics.setContext({ 
          key: 'app_version', 
          value: version, 
          type: 'string' 
        });
      }
    } catch (error) {
      // Silently handle errors
    }
  }

  /**
   * Set the build number for crash reports
   */
  async setBuildNumber(buildNumber) {
    try {
      if (Capacitor.isNativePlatform()) {
        await FirebaseCrashlytics.setContext({ 
          key: 'build_number', 
          value: buildNumber, 
          type: 'string' 
        });
      }
    } catch (error) {
      // Silently handle errors
    }
  }
}

export default CrashlyticsService; 